import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  IonApp,
  IonContent,
  IonRouterOutlet,
  IonFooter,
  IonTabBar,
  IonTabButton,
  IonIcon,
  IonLabel,
  ActionSheetController
} from '@ionic/angular/standalone';
import { HeaderComponent } from './shared/components/header/header.component';
import { SessionService } from './shared/services/session.service';
import { ModalService } from './shared/services/modal.service';
import { Observable, Subscription, map } from 'rxjs';
import { CommonModule } from '@angular/common';
import { Platform } from '@ionic/angular';
import { PushNotificationService } from './shared/services/push-notification.service';
import { Router, RouterModule, RouterLink, RouterLinkActive } from '@angular/router';
import { ActionPerformed, PushNotifications } from '@capacitor/push-notifications';
import { addIcons } from 'ionicons';
import {
  homeOutline,
  searchOutline,
  peopleOutline,
  informationCircleOutline,
  personCircleOutline,
  settingsOutline
} from 'ionicons/icons';
import { TranslatePipe } from './shared/pipes/translate.pipe';
import { AuthService } from './shared/services/auth.service';
import { FilterStateService } from './shared/services/filter-state.service';
import { AdminStateService } from './shared/services/admin-state.service';
import { TranslateService } from './shared/services/translate.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [
    IonApp,
    IonContent,
    IonRouterOutlet,
    IonFooter,
    IonTabBar,
    IonTabButton,
    IonIcon,
    IonLabel,
    HeaderComponent,
    CommonModule,
    RouterModule,
    RouterLink,
    RouterLinkActive,
    TranslatePipe
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private sessionWarningSub: Subscription | undefined;
  private isModalOpen = false;
  private sessionTimeoutModal: HTMLIonModalElement | null = null;
  private sessionModalRequestId = 0;
  isNativeApp = false;

  isAdmin$: Observable<boolean>;

  constructor(
    private sessionService: SessionService,
    private modalService: ModalService,
    private platform: Platform,
    private pushNotificationService: PushNotificationService,
    private router: Router,
    private authService: AuthService,
    private actionSheetCtrl: ActionSheetController,
    private filterStateService: FilterStateService,
    private adminStateService: AdminStateService,
    private translate: TranslateService
  ) {
    addIcons({
      'home-outline': homeOutline,
      'search-outline': searchOutline,
      'people-outline': peopleOutline,
      'information-circle-outline': informationCircleOutline,
      'person-circle-outline': personCircleOutline,
      'settings-outline': settingsOutline
    });

    this.isNativeApp = this.platform.is('capacitor');

    this.isAdmin$ = this.authService.userRoles$.pipe(
      map(roles => roles.includes('super-admin') || roles.includes('org-admin'))
    );

    if (!this.platform.is('mobile')) {
      this.sessionService.addPreLogoutTask(async () => {
        this.sessionModalRequestId++;
        this.isModalOpen = false;

        const modal = this.sessionTimeoutModal;
        this.sessionTimeoutModal = null;
        if (modal) {
          await modal.dismiss();
        }
      });
    }
  }

  ngOnInit() {
    if (!this.platform.is('mobile')) {
      this.sessionWarningSub = this.sessionService.shouldWarnUser().subscribe((shouldWarn: boolean) => {
        if (shouldWarn && !this.isModalOpen) {
          this.openSessionTimeoutModal();
        }
      });
    }
    this.pushNotificationService.initPushNotifications();
    this.addNotificationListener();
  }

  private addNotificationListener(): void {
    if (!this.platform.is('capacitor')) {
      return;
    }

    PushNotifications.addListener('pushNotificationActionPerformed',
      (notification: ActionPerformed) => {
        const linkUrl = notification.notification.data.linkUrl;
        if (linkUrl) {
          const url = new URL(linkUrl);
          this.router.navigateByUrl(url.pathname);
        }
      }
    );
  }

  async openSessionTimeoutModal() {
    if (this.isModalOpen) return;

    const requestId = ++this.sessionModalRequestId;
    this.isModalOpen = true;
    this.sessionService.removeListeners();

    try {
      const modal = await this.modalService.openSessionTimeoutModal(null);
      this.sessionTimeoutModal = modal;

      if (requestId !== this.sessionModalRequestId || !this.authService.getCurrentUser()) {
        await modal.dismiss();
        return;
      }

      await modal.present();

      if (requestId !== this.sessionModalRequestId || !this.authService.getCurrentUser()) {
        await modal.dismiss();
        return;
      }

      const { data } = await modal.onDidDismiss();
      if (data === 'continue') {
        await this.sessionService.refreshSession();
      } else if (data === 'logout') {
        await this.sessionService.endSession();
      }
    } catch (error) {
      console.error("Error handling session timeout modal:", error);
    } finally {
      if (requestId === this.sessionModalRequestId) {
        this.sessionTimeoutModal = null;
        this.isModalOpen = false;

        if (this.authService.getCurrentUser()) {
          this.sessionService.initListeners();
        }
      }
    }
  }

  ngOnDestroy() {
    this.sessionWarningSub?.unsubscribe();
  }
  async showOpportunitiesMenu() {
    const actionSheet = await this.actionSheetCtrl.create({
      header: this.translate.instant('OPPORTUNITIES'),
      buttons: [
        {
          text: this.translate.instant('STUDENTS'),
          handler: () => this.navigateToOpportunity('student')
        },
        {
          text: this.translate.instant('ACADEMIC_STAFF'),
          handler: () => this.navigateToOpportunity('academic-staff')
        },
        {
          text: this.translate.instant('ADMINISTRATIVE_STAFF'),
          handler: () => this.navigateToOpportunity('administrative-staff')
        },
        {
          text: this.translate.instant('NON_UNIVERSITY_STAKEHOLDER'),
          handler: () => this.navigateToOpportunity('non-university-stakeholder')
        }
      ]
    });
    await actionSheet.present();
  }

  private navigateToOpportunity(filter?: string) {
    this.filterStateService.clearFilters();
    if (filter) {
      this.filterStateService.setOpenToFilter([filter]);
    }
    this.router.navigateByUrl('/opportunity');
  }

  async showAdminMenu() {
    const actionSheet = await this.actionSheetCtrl.create({
      header: this.translate.instant('ADMIN'),
      buttons: [
        {
          text: this.translate.instant('ADMIN_DASHBOARD'),
          handler: () => this.selectAdminSection()
        },
        {
          text: this.translate.instant('OPPORTUNITIES'),
          handler: () => this.selectAdminSection({ name: 'Opportunity', role: 'org-admin' })
        },
        {
          text: this.translate.instant('USER_DATA'),
          handler: () => this.selectAdminSection({ name: 'Super Admin', role: 'super-admin' })
        }
      ]
    });
    await actionSheet.present();
  }

  private selectAdminSection(section?: { name: string; role: string; }) {
    if (!section) {
      this.adminStateService.setCurrentSection('');
      this.adminStateService.setSectionToLoad(null);
      this.router.navigate(['/admin']);
      return;
    }
    this.adminStateService.setSectionToLoad(section);
    this.router.navigate(['/admin']);
  }

  isActive(url: string): boolean {
    return this.router.url.startsWith(url);
  }
}
